import { Box, Chip, IconButton, Tooltip } from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import type { FC } from 'react';
import { useCallback } from 'react';

import {
  CreativeAccordion,
  CreativeAccordionSummary,
  CreativeAppName,
  CreativeMetric,
  CreativePaper,
} from 'features/creatives';
import { ExpandMoreOutlined, ZoomOutMapOutlined } from '@mui/icons-material';
import useFacebookAdsLibraryParams from '../hooks/useFacebookAdsLibraryParams';
import type { FacebookCreativeAdByRegionDTO } from '../api/types';
import ContentPreview from './ContentPreview';
import AccordionDetails from './AccordionDetails';
import CreativeDetailsDialog from './CreativeDetailsDialog';
import CreativeUsDetailsDialog from './CreativeUsDetailsDialog';
import { useModal } from '../../../hooks';
import { useQueryClient } from '@tanstack/react-query';
import { getFBCreativeByIdOptions } from '../api/getFBCreativeById';
import { format } from 'date-fns';
import { trackAdsLibraryCreativeView } from 'lib/datadog';

interface CreativeItemProps {
  creative: FacebookCreativeAdByRegionDTO;
}
const CreativeItem: FC<CreativeItemProps> = ({ creative }) => {
  const { allExpanded, startDate, endDate, network, region } = useFacebookAdsLibraryParams();
  const { isOpen: isOpenDialog, open: handleOpenDialog, close: handleCloseDialog } = useModal();
  const [ad] = creative.ads;
  const [expanded, setExpanded] = useState(false);
  const queryClient = useQueryClient();
  const isUsRegion = region === 'us';
  const isEuRegion = region === 'eu';
  const winnerConfidencePercent = creative.winner_confidence_score ? Math.round(creative.winner_confidence_score * 100) : 0;
  const longevityDays = creative.longevity ?? ad.longevity ?? 0;
  const adsCount = creative.ads_count ?? ad.ads_count ?? creative.ads.length;
  const adCreativeBodies = ad.ad_creative_bodies ?? [];
  const adCreativeLinkTitles = ad.ad_creative_link_titles ?? [];
  const totalReachDelta = 'total_reach_delta' in ad ? ad.total_reach_delta : 0;

  const detailsQueryOptions = useMemo(
    () =>
      getFBCreativeByIdOptions({
        id: ad.ad_id,
        date_from: format(startDate, 'yyyy-MM-dd'),
        date_to: format(endDate, 'yyyy-MM-dd'),
        network,
        region,
      }),
    [ad.ad_id, startDate, endDate, network, region]
  );

  const handlePrefetch = useCallback(async () => {
    if (isUsRegion) {
      return;
    }
    await queryClient.prefetchQuery(detailsQueryOptions);
  }, [isUsRegion, queryClient, detailsQueryOptions]);

  useEffect(() => {
    requestAnimationFrame(() => {
      setExpanded(allExpanded);
    });
  }, [allExpanded]);

  const handleExpansion = useCallback(() => {
    setExpanded((prevExpanded) => {
      const nextExpanded = !prevExpanded;
      if (nextExpanded) {
        trackAdsLibraryCreativeView({
          network,
          region,
          viewType: 'accordion_expand',
          creativeId: creative.ad_creative_id,
          adId: ad.ad_id,
          company: ad.company,
          adCreativeType: ad.ad_creative_type,
        });
      }
      return nextExpanded;
    });
  }, [ad.ad_creative_type, ad.ad_id, ad.company, creative.ad_creative_id, network, region]);

  const handleOpenCreativeDialog = useCallback(() => {
    trackAdsLibraryCreativeView({
      network,
      region,
      viewType: 'dialog_open',
      creativeId: creative.ad_creative_id,
      adId: ad.ad_id,
      company: ad.company,
      adCreativeType: ad.ad_creative_type,
    });
    handleOpenDialog();
  }, [ad.ad_creative_type, ad.ad_id, ad.company, creative.ad_creative_id, handleOpenDialog, network, region]);

  return (
    <>
      <CreativePaper onMouseEnter={handlePrefetch} id={`fb-ads-creative-${creative.ad_creative_id}-paper`}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'start',
            justifyContent: 'start',
            p: 1,
            gap: 0.75,
          }}
        >
          <Box
            sx={{
              display: 'flex',
              width: '100%',
              flexDirection: 'row',
              alignItems: 'start',
              justifyContent: 'space-between',
              gap: 0.75,
            }}
          >
            <Box
              sx={{
                flex: 1,
                maxWidth: '35%',
                display: 'flex',
                flexWrap: 'nowrap',
                justifyContent: 'start',
                position: 'relative',
              }}
            >
              <Tooltip title={ad.company || 'Unknown company'}>
                <CreativeAppName>{ad.company || 'Unknown company'}</CreativeAppName>
              </Tooltip>
            </Box>
            <Box sx={{ flex: 1, display: 'flex', flexWrap: 'nowrap', justifyContent: 'end' }}>
              {isUsRegion && (
                <Box
                  sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'nowrap', justifyContent: 'end' }}
                >
                  <Chip
                    size="small"
                    label={`${winnerConfidencePercent}%`}
                    sx={{ backgroundColor: '#E8F5E9', color: '#2E7D32', fontWeight: 600, borderRadius: 1 }}
                  />
                  <Chip
                    size="small"
                    label={`${longevityDays}d`}
                    sx={{ backgroundColor: '#E3F2FD', color: '#1565C0', fontWeight: 600, borderRadius: 1 }}
                  />
                  <Chip
                    size="small"
                    label={`${adsCount} ads`}
                    sx={{ backgroundColor: '#F3E5F5', color: '#6A1B9A', fontWeight: 600, borderRadius: 1 }}
                  />
                </Box>
              )}
              {isEuRegion && (
                <CreativeMetric
                  type="reach"
                  title="Total reach delta"
                  value={new Intl.NumberFormat('en-US').format(totalReachDelta)}
                />
              )}
            </Box>
          </Box>
          <CreativeAccordion expanded={expanded} onChange={handleExpansion}>
            <Box
              sx={{
                display: 'flex',
                width: '100%',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 2,
              }}
            >
              <IconButton onClick={handleOpenCreativeDialog} size={'small'} sx={{ p: 0 }}>
                <ZoomOutMapOutlined fontSize={'small'} />
              </IconButton>
              <CreativeAccordionSummary>
                <IconButton size={'small'} sx={{ p: 0 }}>
                  <ExpandMoreOutlined
                    sx={{ transition: 'transform 0.2s', transform: `rotate(${expanded ? 180 : 0}deg)` }}
                  />
                </IconButton>
              </CreativeAccordionSummary>
            </Box>
            <AccordionDetails
              isUsRegion={isUsRegion}
              page_name={ad.page_name}
              ad_creative_bodies={adCreativeBodies}
              ad_creative_link_titles={adCreativeLinkTitles}
              branch={ad.branch}
              ad_delivery_start_time={creative.ad_delivery_start_time}
              ad_delivery_stop_time={creative.ad_delivery_stop_time}
              scraped_link={ad.scraped_link}
            />
          </CreativeAccordion>
        </Box>
        <Box sx={{ position: 'relative', display: 'flex', flexGrow: 1 }}>
          <ContentPreview
            ad_library_url={ad.ad_library_url}
            video_preview_image_url={ad.video_preview_image_url}
            original_image_url={ad.original_image_url}
            video_sd_url={ad.video_sd_url}
            ad_id={ad.ad_id}
          />
        </Box>
        {isEuRegion && <CreativeDetailsDialog adId={ad.ad_id} open={isOpenDialog} onClose={handleCloseDialog} />}
        {isUsRegion && (
          <CreativeUsDetailsDialog
            open={isOpenDialog}
            onClose={handleCloseDialog}
            adCreativeId={creative.ad_creative_id}
            creative={creative}
            winnerConfidencePercent={winnerConfidencePercent}
            longevityDays={longevityDays}
            adsCount={adsCount}
          />
        )}
      </CreativePaper>
    </>
  );
};

export default CreativeItem;
